import { updateProfile, getOwnProfile } from "../../api/directory_api";
import { CompanyUser } from "@models/user";
import {
    WithLoggedInAuthInfoProps,
    withRequiredAuthInfo,
} from "@propelauth/react";
import { useState, useEffect } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { AutosizeTextarea } from "../ui/autoresizeTextArea";
import { MultiSelectCombobox } from "../ui/comboBox";
import {USConstructionTypes} from "../../contexts/ConstructionType";
import {useToast} from "../ui/use-toast";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";


function OwnUserProfile(props: WithLoggedInAuthInfoProps) {
    const navigate = useNavigate();
    const {toast} = useToast();
    const [error, setError] = useState<any>(null);
    const [profileData, setProfileData] = useState<CompanyUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [openConstruction, setOpenConstruction] = useState(false);
    const [openMarkets, setOpenMarkets] = useState(false);
    const [openRegions, setOpenRegions] = useState(false);

    const methods = useForm<CompanyUser>();

    const { reset } = methods;

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const data = await getOwnProfile(props.accessToken);
                setProfileData(data);
                reset(data);
                setLoading(false);
            } catch (error) {
                setError(error);
                setLoading(false);
            }
        };
        fetchProfile();
    }, [props.accessToken, reset]);

    console.log(profileData);

    const onSubmit: SubmitHandler<CompanyUser> = (data: CompanyUser) => {
        updateProfile(data.user, props.accessToken)
            .then(() => {
                window.location.reload();
            })
            .catch((error) => {
                setError(error);
                toast({
                    title: "Error",
                    description: "Error updating Profile: " + String(error),
                    variant: "destructive",
                });
            });
    };

    const [editingEnabled, setEditingEnabled] = useState<boolean>(false);
    const onChangeEvent = (e: any) => {
        setEditingEnabled(e.target.checked);
    };

    const constructionTypes = USConstructionTypes.map((x) => ({ value: x, label:x }))

    const marketTypes = [
        { value: "USA", label: "USA" },
        { value: "Europe", label: "Europe" },
        { value: "Asia", label: "Asia" },
    ];

    const regionTypes = [
        { value: "North", label: "North" },
        { value: "South", label: "South" },
        { value: "East", label: "East" },
        { value: "West", label: "West" },
    ];

    if (loading) {
        return <div><BaseLoadingSpinner/></div>;
    }

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="p-6 bg-white rounded shadow-md max-w-6xl mx-auto"
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Your Profile</h3>
                    <div className="flex items-center">
                        <label htmlFor="editing" className="mr-2 font-medium">
                            Editing:{" "}
                        </label>
                        <input
                            id="editing"
                            type="checkbox"
                            checked={editingEnabled}
                            onChange={(e) => onChangeEvent(e)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        name="company.name"
                        render={({ field }) => (
                            <FormItem className="col-span-2">
                                <FormLabel>Company</FormLabel>
                                <FormControl>
                                    <div className="mt-2">
                                        <span>{field.value || "N/A"}</span>
                                    </div>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.email"
                        render={({ field }) => (
                            <FormItem className="col-span-2">
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                    <div className="mt-2">
                                        <span>{field.value || "N/A"}</span>
                                    </div>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <Input
                                            className="w-full"
                                            disabled={!editingEnabled}
                                            {...field}
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>{field.value || "N/A"}</span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.phone"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Phone</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <Input
                                            className="w-full"
                                            disabled={!editingEnabled}
                                            {...field}
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>{field.value || "N/A"}</span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.title"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Title</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <Input
                                            className="w-full"
                                            disabled={!editingEnabled}
                                            {...field}
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>{field.value || "N/A"}</span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.construction_types"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Construction Types</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <MultiSelectCombobox
                                            types={constructionTypes}
                                            open={openConstruction}
                                            setOpen={setOpenConstruction}
                                            value={field.value}
                                            onChange={(newValue) =>
                                                field.onChange(newValue)
                                            }
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>
                                                {field.value.length > 0
                                                    ? field.value.join(", ")
                                                    : "N/A"}
                                            </span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.markets"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Markets</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <MultiSelectCombobox
                                            types={marketTypes}
                                            open={openMarkets}
                                            setOpen={setOpenMarkets}
                                            value={field.value}
                                            onChange={(newValue) =>
                                                field.onChange(newValue)
                                            }
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>
                                                {field.value.length > 0
                                                    ? field.value.join(", ")
                                                    : "N/A"}
                                            </span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.regions"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Regions</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <MultiSelectCombobox
                                            types={regionTypes}
                                            open={openRegions}
                                            setOpen={setOpenRegions}
                                            value={field.value}
                                            onChange={(newValue) =>
                                                field.onChange(newValue)
                                            }
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>
                                                {field.value.length > 0
                                                    ? field.value.join(", ")
                                                    : "N/A"}
                                            </span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name="user.description"
                        render={({ field }) => (
                            <FormItem className="col-span-2">
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                    {editingEnabled ? (
                                        <AutosizeTextarea
                                            className="w-full min-h-20"
                                            disabled={!editingEnabled}
                                            {...field}
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <span>{field.value || "N/A"}</span>
                                        </div>
                                    )}
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>

                {editingEnabled && (
                    <div className="mt-6 flex justify-end">
                        <Button type="submit" variant="secondary">
                            Save
                        </Button>
                    </div>
                )}
            </form>
        </FormProvider>
    );
}

export default withRequiredAuthInfo(OwnUserProfile);
